import classNames from 'clsx';
import React, { memo, useCallback } from 'react';
import { multiTariffHighlightMobile, multiTariffVerticalMobile } from 'reactApp/appHelpers/featuresHelpers/features/multiTariffMobile';
import { renderFeaturesListMobileModal } from 'reactApp/sections/MobileQuotaTariffsLanding/FeaturesModal/FeaturesModal.helpers';
import type { MobileBuyClickType } from 'reactApp/sections/MobileSubscriptionsPage/TariffCard/TariffCard.types';
import { FeatureIDs } from 'reactApp/sections/QuotaLanding/MultiTariffsListMobile/Features/Features.types';
import { useFeaturesList } from 'reactApp/sections/QuotaLanding/MultiTariffsListMobile/Features/hooks/useFeaturesList';
import { sendQuotaGa } from 'reactApp/sections/QuotaLanding/QuotaLanding.helpers';
import type { Product } from 'reactApp/types/Billing';
import { openUnlimitedUploadInfo } from 'reactApp/ui/Mobile/UnlimitedUploadInfo/UnlimitedUploadInfo.helpers';
import { ETabsName } from 'reactApp/ui/TariffSectionNew/TariffSection.constants';

import styles from './Features.css';

interface FeaturesProps {
    space: number;
    product?: Product;
    onBuyClick: MobileBuyClickType;
    activeTariffTab?: ETabsName;
    showDivider?: boolean;
    blackFriday2025Enabled?: boolean;
    marchPromo2026Enabled?: boolean;
}

export const Features = memo<FeaturesProps>(
    ({ space, product, onBuyClick, activeTariffTab, showDivider, blackFriday2025Enabled = false, marchPromo2026Enabled = false }) => {
        const list = useFeaturesList(space);

        const handleClick = useCallback(
            (id: string) => {
                if (id === FeatureIDs.unlim) {
                    sendQuotaGa({
                        action: 'tariff_baz',
                        label: 'click',
                    });
                    openUnlimitedUploadInfo();
                }

                if (id === FeatureIDs.others) {
                    sendQuotaGa({
                        action: 'tariff_benefits',
                        label: 'click',
                        name_button_from: activeTariffTab,
                    });
                    renderFeaturesListMobileModal({ product, onBuyClick, from: activeTariffTab });
                }
            },
            [product, activeTariffTab, onBuyClick]
        );

        return (
            <div
                className={classNames(styles.root, {
                    [styles.rootDivider]: Boolean(showDivider),
                    [styles.multiTariffVerticalPadding]: multiTariffVerticalMobile,
                })}
            >
                {list.map(({ id, type, text, iconBefore, iconAfter }) => {
                    return (
                        <div
                            key={id + type}
                            className={classNames(styles.item, {
                                [styles[`item-${type}`]]: type,
                                [styles['item-violet']]:
                                    multiTariffHighlightMobile && activeTariffTab === ETabsName.family && type === 'highlighted',
                                [styles['item-sky']]:
                                    multiTariffHighlightMobile && activeTariffTab === ETabsName.for_work && type === 'highlighted',
                                [styles['item-blackFriday2025']]:
                                    multiTariffHighlightMobile &&
                                    activeTariffTab === ETabsName.advanced &&
                                    type === 'highlighted' &&
                                    blackFriday2025Enabled,
                                [styles.itemMarchPromo2026]: multiTariffHighlightMobile && marchPromo2026Enabled && type === 'highlighted',
                            })}
                            onClick={() => handleClick(id)}
                            data-qa-type={type}
                        >
                            <div className={styles.iconContainer}>{iconBefore}</div>
                            {text}
                            <div className={styles.iconContainer}>{iconAfter}</div>
                        </div>
                    );
                })}
            </div>
        );
    }
);

Features.displayName = 'Features';
